package org.moxultimate.topwithmox.components.image

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.PANORAMA_VIEW_TOGGLE
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse

/**
 * WallpaperDetailDisplay
 * Shows the full-resolution wallpaper image alongside its metadata and download button.
 * On mobile: stacks image above text in a Column. On desktop: side-by-side Row layout.
 *
 * @param wallpaper - The wallpaper to display
 * @param modifier - Outer modifier applied to the root layout container
 * @param onDownloadClick - Callback fired when the DOWNLOAD button is pressed
 * @param onOpen360Click - Callback fired when the Open 360 View button is pressed
 */
@Composable
fun WallpaperDetailDisplay(
    wallpaper: WallpaperResponse,
    modifier: Modifier = Modifier,
    onDownloadClick: () -> Unit,
    onOpen360Click: () -> Unit,
) {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM

    // True only when rendering a phone-type wallpaper in mobile view — needs centering
    val isMobilePhone = isMobile && wallpaper.type.lowercase() == "phone"

    // Width-only for all types — height auto-scales from the image's natural ratio
    val wallpaperModifier = remember(wallpaper.type, isMobile) {
        if (isMobile) {
            when (wallpaper.type.lowercase()) {
                // Phone: constrained portrait width so it doesn't stretch edge-to-edge; centered by parent
                "phone" -> Modifier.width(clamp(min = 200.px, value = 60.vw, max = 280.px))
                "desktop" -> Modifier.fillMaxWidth()
                "widescreen" -> Modifier.fillMaxWidth()
                "vertical" -> Modifier.fillMaxWidth()
                else -> Modifier.fillMaxWidth()
            }
        } else {
            when (wallpaper.type.lowercase()) {
                "phone" -> Modifier.width(clamp(min = 360.px, value = 8.5.vw, max = 760.px))
                "desktop" -> Modifier.width(clamp(min = 720.px, value = 16.vw, max = 1020.px))
                "widescreen" -> Modifier.width(clamp(min = 800.px, value = 16.vw, max = 1100.px))
                "vertical" -> Modifier.width(clamp(min = 460.px, value = 8.5.vw, max = 860.px))
                else -> Modifier.width(clamp(min = 400.px, value = 8.5.vw, max = 800.px))
            }
        }
    }

    val imageNode = @Composable {
        Image(
            src = wallpaper.thumbnailPath?.takeIf { it.isNotBlank() } ?: wallpaper.imagePath.orEmpty(),
            description = "${wallpaper.name} — ${wallpaper.type} 4K wallpaper from the ${wallpaper.series} series",
            modifier = wallpaperModifier
                .borderRadius(16.px)
                .onContextMenu { e -> e.preventDefault() }
                .onDragStart { e -> e.preventDefault() }
        )
    }

    val detailsNode = @Composable {
        WallpaperDetailsText(
            wallpaper = wallpaper,
            isMobile = isMobile,
            isMobilePhone = isMobilePhone,
            onDownloadClick = onDownloadClick,
            onOpen360Click = onOpen360Click,
        )
    }

    if (isMobile) {
        // Stack image above details; center horizontally for phone (portrait) type
        Column(
            modifier = modifier.fillMaxWidth().padding(bottom = 16.px),
            horizontalAlignment = if (isMobilePhone) Alignment.CenterHorizontally else Alignment.Start,
        ) {
            imageNode()
            detailsNode()
        }
    } else {
        Row(modifier.padding(bottom = 16.px)) {
            imageNode()
            detailsNode()
        }
    }
}


/**
 * WallpaperDetailsText
 * Displays wallpaper name, series, type, resolution, and the download button.
 * Adjusts layout margin and font sizes based on [isMobile].
 *
 * @param wallpaper - Source data
 * @param isMobile - True when rendering on a small screen
 * @param isMobilePhone - True when rendering a phone-type wallpaper on mobile
 * @param onDownloadClick - Propagated from parent to the download button
 * @param onOpen360Click - Propagated from parent to the Open 360 View button
 */
@Composable
private fun WallpaperDetailsText(
    wallpaper: WallpaperResponse,
    isMobile: Boolean,
    isMobilePhone: Boolean = false,
    onDownloadClick: () -> Unit,
    onOpen360Click: () -> Unit,
) {
    val textAlign = if (isMobile) TextAlign.Center else TextAlign.Start

    val textColumnModifier = if (isMobile) {
        Modifier.fillMaxWidth().margin(top = 20.px).padding(bottom = 16.px)
    } else {
        // No fillMaxHeight() — inheriting 100vh from the scroll container pushed the
        // download button far off-screen. Let the column size to its content instead.
        Modifier.margin(left = 30.px)
    }

    val titleFontSize = if (isMobile) clamp(min = 20.px, value = 5.vw, max = 24.px)
    else clamp(min = 22.px, value = 0.8.vw, max = 24.px)

    val bodyFontSize = if (isMobile) clamp(min = 14.px, value = 3.5.vw, max = 18.px)
    else clamp(min = 16.px, value = 0.8.vw, max = 18.px)

    Column(
        modifier = textColumnModifier,
        horizontalAlignment = if (isMobile) Alignment.CenterHorizontally else Alignment.Start,
    ) {

        SpanText(
            text = wallpaper.name,
            modifier = Modifier
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(titleFontSize)
                .fontWeight(FontWeight.SemiBold)
                .textAlign(textAlign)
                .moxTextGradient()
        )

        SpanText(
            text = wallpaper.series + " Series",
            modifier = Modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(bodyFontSize)
                .fontWeight(FontWeight.Normal)
                .textAlign(textAlign)
        )

        SpanText(
            text = wallpaper.type + " Wallpaper",
            modifier = Modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(bodyFontSize)
                .fontWeight(FontWeight.Normal)
                .textAlign(textAlign)
        )

        SpanText(
            text = "4KHD Wallpaper",
            modifier = Modifier
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .letterSpacing(0.8.px)
                .fontSize(bodyFontSize)
                .fontWeight(FontWeight.Normal)
                .textAlign(textAlign)
        )

        GradientButton(
            text = "DOWNLOAD",
            modifier = Modifier.margin(top = 24.px),
            onClick = { onDownloadClick() }
        )

        val has360View = !wallpaper.panoramaPath.isNullOrBlank()
        if (has360View && PANORAMA_VIEW_TOGGLE) {
            GradientButton(
                text = "Open 360 View",
                modifier = Modifier.margin(top = 12.px),
                onClick = { onOpen360Click() }
            )
        }
    }
}
